import { useEffect, useState } from 'react';
import { ArrowLeftIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatCompactCurrency } from '../../Utils';
import { useCustomerLookup, useCustomerDetails } from '../../hooks/reactQuery/useCustomer';
import { useMerchantSettings } from '../../hooks/reactQuery/useMerchant';
import { useBrand } from '../../Context/Brand/BrandContext';
import LoginModal from '../../components/shared/LoginModal';
import MessagePreferences from '../../components/shared/MessagePreferences';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-gray-100 text-gray-600',
  preparing: 'bg-gray-100 text-gray-600',
  ready: 'bg-gray-100 text-gray-600',
  delivered: 'bg-gray-100 text-gray-600',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-gray-100 text-gray-600',
};

const TABS = [
  { id: 'orders', label: 'Orders' },
  { id: 'points', label: 'Points' },
  { id: 'impact', label: 'Impact' },
  { id: 'activity', label: 'Activity' },
  { id: 'messages', label: 'Messages' },
];

export default function UserAccountPage() {
  const platformBrand = usePlatformBrand();
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [showLogin, setShowLogin] = useState(false);

  const customerName = localStorage.getItem('customer_name') || '';
  const customerPhone = localStorage.getItem('customer_phone') || '';
  const merchantId = localStorage.getItem('merchant_id') || '';
  const storedCustomerId = localStorage.getItem('customer_id') || '';
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const userId = storedUser?.id || null;
  const isGuest = !userId;

  // Sanitize phone for lookup
  const cleanPhone = customerPhone.replace(/\D/g, '');

  // Guest orders are still looked up by phone/customer id, but points can only be redeemed after sign-in.
  const { data: customerBasic, isLoading: loadingLookup } = useCustomerLookup(
    merchantId,
    (isGuest || !storedCustomerId) ? cleanPhone : null
  );
  const targetId = storedCustomerId || customerBasic?.id;

  useEffect(() => {
    if (!storedCustomerId && customerBasic?.id) {
      localStorage.setItem('customer_id', customerBasic.id);
    }
  }, [customerBasic?.id, storedCustomerId]);
  
  const { data: customerFull, isLoading: loadingDetails } = useCustomerDetails(targetId, {
    restaurantId: merchantId,
    userId,
    enabled: !isGuest,
  });
  const { data: settings, isLoading: loadingSettings } = useMerchantSettings(merchantId);
  const rawPointValue = Number(settings?.point_value_shillings);
  const hasPointValueSetting = settings?.point_value_shillings !== undefined
    && settings?.point_value_shillings !== null
    && Number.isFinite(rawPointValue)
    && rawPointValue > 0;
  const pointValueShillings = hasPointValueSetting ? rawPointValue : 0;

  // If we skipped lookup, only wait for details to load
  const isLoading = isGuest
    ? loadingLookup
    : (storedCustomerId ? loadingDetails : (loadingLookup || loadingDetails));

  const customer = customerFull?.customer || customerBasic;
  const orders = customerFull?.orders || [];
  const pointsHistory = customerFull?.pointsHistory || [];
  const stats = customerFull?.stats || {};
  const currentRestaurant = customerFull?.currentRestaurant || {};
  const supuImpact = customerFull?.supuImpact || null;
  const restaurantBreakdown = customerFull?.restaurantBreakdown || [];

  const pointsBalance = currentRestaurant?.pointsBalance ?? customer?.total_points ?? 0;
  const totalOrders = (stats.totalOrders ?? orders?.length) || 0;
  const completedOrders = (stats.completedOrders ?? (orders || []).filter(o => o.status === 'completed' || o.status === 'delivered').length);
  const totalSpent = (stats.totalSpent ?? (orders || []).reduce((s, o) => s + parseFloat(o.total_amount || o.total || 0), 0));

  const initials = (customerName || 'G').split(' ').map(s => s[0]).join('').toUpperCase().slice(0, 2);

  const handleLogout = () => {
    localStorage.removeItem('customer_name');
    localStorage.removeItem('customer_phone');
    localStorage.removeItem('customer_id');
    localStorage.removeItem('tokens');
    localStorage.removeItem('user');
    Cookies.remove('customer_name');
    Cookies.remove('customer_phone');
    // Navigate back to scanner/landing page depending on where they came from
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gray-50 px-5 pt-12 pb-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
              <ArrowLeftIcon className="w-4 h-4 text-gray-500" />
            </button>
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-1.5 text-[11px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors active:scale-95"
            >
              <ArrowRightOnRectangleIcon className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-500">
              {initials}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{customerName || 'Guest'}</h1>
              <p className="text-sm text-gray-400">{isGuest ? 'Guest profile' : 'Account profile'}{customerPhone ? ` · ${customerPhone}` : ''}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 grid grid-cols-4 gap-1 mt-4">
          {[
            { label: 'Orders', value: totalOrders },
            { label: 'Done', value: completedOrders },
            { label: 'Here', value: formatCompactCurrency(pointsBalance), sub: 'Points redeemable at this restaurant' },
            { label: 'Spent', value: formatCompactCurrency(totalSpent), sub: formatCurrency(totalSpent) },
          ].map(stat => (
            <div key={stat.label} className="text-center min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate" title={stat.sub || String(stat.value)}>{stat.value}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 mt-4">
        <div className="flex overflow-x-auto border-b border-gray-100 gap-4">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`shrink-0 pb-2.5 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: accentColor }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pb-8 mt-4">
        {activeTab === 'messages' && (
          <MessagePreferences key={`${merchantId}:${targetId}`} restaurantId={merchantId} customerId={targetId}
            restaurantName={brand?.restaurantName} accentColor={accentColor} isGuest={isGuest} onSignIn={() => setShowLogin(true)} />
        )}
        {activeTab === 'orders' && (
          isGuest && !isLoading && !customer ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Guest orders</h3>
              <p className="text-sm text-gray-400 text-center mb-4">Orders and points are saved to this phone number. Create one {platformBrand.name} account to keep your visits across restaurants.</p>
              <button onClick={() => setShowLogin(true)} className="px-6 py-2.5 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                style={{ backgroundColor: accentColor }}>
                Create account
              </button>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 mb-4" style={{ borderTopColor: accentColor }} />
              <p className="text-sm text-gray-400">Loading orders...</p>
            </div>
          ) : orders?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No orders yet</h3>
              <p className="text-sm text-gray-400 text-center mb-4">Your order history will appear here once you place your first order.</p>
              <button onClick={() => navigate(-1)} className="px-6 py-2.5 text-white text-sm font-medium rounded-xl transition-colors"
                style={{ backgroundColor: accentColor }}>
                Browse Menu
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {orders?.map(order => {
                const statusColor = STATUS_COLORS[order.status?.toLowerCase()] || 'bg-gray-100 text-gray-600';
                return (
                  <div key={order.id} className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-400">#{order.order_number || order.id?.slice(0, 8)}</p>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
                        {order.status}
                      </span>
                    </div>
                    {(order.items || order.order_items || []).slice(0, 3).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1">
                        <span className="text-gray-700">{item.name || item.item_name} x{item.quantity || item.qty}</span>
                        <span className="text-gray-500">{formatCurrency(item.price || item.unit_price || 0)}</span>
                      </div>
                    ))}
                    {(order.items || order.order_items || []).length > 3 && (
                      <p className="text-xs text-gray-400 mt-1">+{order.items.length - 3} more items</p>
                    )}
                    <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-50">
                      <p className="text-xs text-gray-400">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(order.total_amount || order.total || 0)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'points' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
              <p className="text-3xl font-bold text-gray-900">{pointsBalance}</p>
              <p className="text-sm text-gray-400 mt-1">{brand?.name || 'Restaurant'} points</p>
              {customer && pointsBalance > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Redeemable at this restaurant only
                </p>
              )}
            </div>
            {settings?.rewards_enabled && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">{isGuest ? 'About guest points' : 'How points work'}</p>
                <ul className="space-y-2 text-xs text-gray-500">
                  {isGuest && settings?.guest_points_enabled === false && (
                    <li className="flex items-start gap-2 font-medium text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0" />
                      This restaurant awards new points after you create or sign in to a {platformBrand.name} account.
                    </li>
                  )}
                  {isGuest && (
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                      Create an account with this same phone number to keep your visits across {platformBrand.name}.
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                    Points shown here are redeemable only at this restaurant.
                  </li>
                  {!loadingSettings && (
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                      {hasPointValueSetting
                        ? `This restaurant set 1 point = ${formatCurrency(pointValueShillings)} when redeemed here.`
                        : 'This restaurant has not set a point redemption value yet.'}
                    </li>
                  )}
                  {(!isGuest || settings?.guest_points_enabled !== false) && <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                    Earn {settings?.points_per_order || 1} point per order + {settings?.points_per_currency || 10} points per UGX 100 spent
                  </li>}
                  {(!isGuest || settings?.guest_points_enabled !== false) && <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                    Get {settings?.referral_bonus_points || 50} points when your referral orders
                  </li>}
                  {(!isGuest || settings?.guest_points_enabled !== false) && <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                    Earn {settings?.shoutout_share_points ?? Math.min(Number(settings?.shoutout_points ?? 25), 10)} points when you share a Shout-Out, and {settings?.shoutout_order_points ?? settings?.shoutout_points ?? 25} points when someone orders through it.
                  </li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'impact' && (
          isGuest ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L21 10.5m0 0l-3.75 3.75M21 10.5H9.75M6.75 3.75h-1.5A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25h1.5" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Keep your {platformBrand.name} impact</h3>
              <p className="text-sm text-gray-400 text-center mb-4">Create one {platformBrand.name} account to keep your restaurants, Shout-Outs, and impact together. Points still stay with each restaurant.</p>
              <button onClick={() => setShowLogin(true)} className="px-6 py-2.5 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                style={{ backgroundColor: accentColor }}>
                Create account
              </button>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 mb-4" style={{ borderTopColor: accentColor }} />
              <p className="text-sm text-gray-400">Loading impact...</p>
            </div>
          ) : !supuImpact ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v7.5m3.75-3.75h-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No impact yet</h3>
              <p className="text-sm text-gray-400 text-center">Order and share your favorite restaurants to start building your {platformBrand.name} impact.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Your {platformBrand.name} impact</p>
                <h3 className="text-2xl font-semibold text-gray-900 mt-1">{supuImpact.recognitionTitle || 'Local Starter'}</h3>
                <p className="text-sm text-gray-500 mt-2">Your support across local restaurants on {platformBrand.name}.</p>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {[
                    { label: 'Restaurants', value: supuImpact.restaurantsSupported || 0 },
                    { label: 'Shout-Outs', value: supuImpact.shoutoutsShared || 0 },
                    { label: 'Friends ordered', value: supuImpact.ordersIntroduced || 0 },
                    { label: 'Helped generate', value: formatCompactCurrency(supuImpact.revenueGenerated || 0), title: formatCurrency(supuImpact.revenueGenerated || 0) },
                  ].map(item => (
                    <div key={item.label} className="rounded-xl bg-gray-50 p-3 min-w-0">
                      <p className="text-base font-semibold text-gray-900 truncate" title={item.title || String(item.value)}>{item.value}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900">By restaurant</p>
                  <p className="text-[11px] text-gray-400">Points stay local</p>
                </div>
                <div className="space-y-3">
                  {restaurantBreakdown.map(row => (
                    <div key={row.customerId} className="rounded-xl border border-gray-100 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{row.restaurantName}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {row.pointsBalance || 0} pts here - {row.orders || 0} orders - {row.shoutouts || 0} Shout-Outs
                          </p>
                        </div>
                        {row.isCurrentRestaurant && (
                          <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-1 shrink-0">Here</span>
                        )}
                      </div>
                      {(row.ordersIntroduced > 0 || row.generatedRevenue > 0) && (
                        <p className="text-xs text-gray-500 mt-2">
                          Helped bring {row.ordersIntroduced || 0} orders worth {formatCurrency(row.generatedRevenue || 0)}.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        )}

        {activeTab === 'activity' && (
          isGuest && !isLoading && !customer ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Create an account</h3>
              <p className="text-sm text-gray-400 text-center mb-4">Use the same phone number to keep this restaurant’s points and connect your impact across {platformBrand.name}.</p>
              <button onClick={() => setShowLogin(true)} className="px-6 py-2.5 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                style={{ backgroundColor: accentColor }}>
                Create account
              </button>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 mb-4" style={{ borderTopColor: accentColor }} />
              <p className="text-sm text-gray-400">Loading activity...</p>
            </div>
          ) : pointsHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No activity yet</h3>
              <p className="text-sm text-gray-400 text-center">Points earned from orders and referrals will show up here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pointsHistory.map((entry, i) => {
                const isPositive = entry.points > 0;
                return (
                  <div key={entry.id || i} className="bg-white rounded-xl border border-gray-100 p-3.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{entry.description || entry.type}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <span className={`text-sm font-medium ${isPositive ? 'text-gray-900' : 'text-gray-400'}`}>
                      {isPositive ? '+' : ''}{entry.points} pts
                    </span>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onLoginSuccess={() => window.location.reload()}
          restaurantId={merchantId}
          restaurantName={brand?.name || platformBrand.name}
        />
      )}
    </div>
  );
}
